import { Result } from '@youhelp-ru/core';
import { useCallback } from 'react';
// import { OAuthVariants } from '@common/model/client/oauth/oauthService';
import { IoContainer, useInjection, useIoContainer } from '@cp/shared/di';
import { IModalManager, useManager, useModalHandler } from '@cp/shared/modal';
import { coreCurrentClientApi } from '@cp/entities/client';
// import { detectAppleDevice } from 'src/shared/mediaQueries';
import { useTrackEvent } from 'src/shared/tracking';
import { AppleButton, EmailButton, GoogleButton, PhoneButton, TelegramButton } from '../ui/loginButtons';
import { REGISTRATION_FEATURE } from '../di';
import { AuthResult, AuthVariantSelected, LOGIN_VARIANTS_MODAL } from '../lib';
import { AuthError, unknownToAuthError } from './AuthError';
import { AuthFeature, AuthOption, AuthStrategyImpl, LoginStrategy, SelectedAuthOption } from './types';
import { useAuthService } from './useAuthService';
import { createOAuthStrategy } from './useOauthStrategy';

const getStrategyImplementation = (container: IoContainer, option: SelectedAuthOption): AuthStrategyImpl => {
  switch (option.strategy) {
    case LoginStrategy.Phone:
      return (manager: IModalManager) => import('./usePhoneStrategy').then((module) => module.phoneStrategy(manager));
    case LoginStrategy.Email:
      return (manager: IModalManager) => import('./useEmailStrategy').then((module) => module.emailStrategy(manager));
    case LoginStrategy.OAuth:
      if (!option.provider) {
        throw new AuthError('Provider required');
      }
      return createOAuthStrategy(container, option.provider);
    default:
      throw new AuthError('Unknown auth strategy');
  }
};

export const useAuth = (): AuthFeature => {
  const modalManager = useManager();
  const openAuthModal = useModalHandler(LOGIN_VARIANTS_MODAL);
  const registrationFeature = useInjection(REGISTRATION_FEATURE);
  const authService = useAuthService();
  const [getProfile] = coreCurrentClientApi.endpoints.getProfile.useLazyQuery();
  const container = useIoContainer();
  const trackEvent = useTrackEvent();

  return useCallback(
    async (clientSelection) => {
      console.debug('Start auth feature');
      let variant: SelectedAuthOption | null = null; // сохраняем выбранный вариант, чтобы позже отправить в метрику
      const options: AuthOption[] = [
        // { strategy: LoginStrategy.OAuth, provider: OAuthVariants.Google, enabled: true, Component: GoogleButton },
        // { strategy: LoginStrategy.OAuth, provider: OAuthVariants.Apple, enabled: detectAppleDevice(), Component: AppleButton },
        { strategy: LoginStrategy.Phone, provider: null, enabled: true, Component: PhoneButton },
        { strategy: LoginStrategy.Email, provider: null, enabled: true, Component: EmailButton },
        // { strategy: LoginStrategy.OAuth, provider: OAuthVariants.Telegram, enabled: false, Component: TelegramButton },
      ];
      const authModal = await openAuthModal({ options });
      const selectedOption = await Result.fromAsync(() => authModal.promise);
      const runSelectedStrategy = () =>
        selectedOption
          .tap((v) => {
            variant = v;
            trackEvent(AuthVariantSelected({ variant }));
          })
          .mapError(unknownToAuthError)
          .map(getStrategyImplementation.bind(null, container))
          .flatMapAsync((strategyImpl) => strategyImpl(modalManager));

      let auth = await runSelectedStrategy();

      while (auth.isErr && auth.error?.message === 'Client requested change OTP resource') {
        auth = await runSelectedStrategy();
      }

      return auth
        .mapError((err) => {
          // Oauth providers should be shown while navigation on slow connection
          if (err.message === 'OAuthRedirect') {
            window.addEventListener(
              'pageshow',
              (e) => {
                if (e.persisted) {
                  console.debug('Close authVariants modal by page navigation');
                  authModal.close();
                }
              },
              { once: true },
            );
            return new AuthError('Cancelled by client navigation');
          }
          trackEvent(AuthResult({ variant, error: err }));
          return err;
        })
        .flatMapAsync(async (clientAuthResponse) => {
          if (clientAuthResponse.isRegistered && clientAuthResponse.token) {
            authService.authorize(clientAuthResponse.token);
            const profile = await Result.fromAsync(() => getProfile().unwrap());
            return profile.mapError(unknownToAuthError).tap(({ type }) => {
              trackEvent(AuthResult({ isRegistered: clientAuthResponse.isRegistered, variant, clientType: type }));
            });
          } else if (clientAuthResponse.registrationSessionId) {
            console.debug('auth trigger registration');
            trackEvent(AuthResult({ isRegistered: clientAuthResponse.isRegistered, variant }));
            const registrationResult = await registrationFeature(clientAuthResponse.registrationSessionId, {
              mainCareerId: clientSelection?.careerId,
              ...clientSelection,
              ...clientAuthResponse.state,
            });
            return registrationResult.mapError(unknownToAuthError);
          } else {
            return Result.Err(new AuthError('No registration session found. (Maybe OAuth used without redirect)'));
          }
        });
    },
    [container, registrationFeature, modalManager, authService, getProfile, openAuthModal],
  );
};
